import { Rng } from './rng';
import { forcaPorHabilidade, modoHabilidadeLigado } from './habilidades';
import { disponivel, escalarAutoFormacao, fatorPosicao, forcaBase, forcaComEnergia, LINHAS, resolverEscalacao, slotsDaFormacao } from './tatica';
import {
  ARQUETIPOS,
  PAPEIS,
  aptidaoPapel,
  arquetipoHabitual,
  condicaoDaEscalacao,
  condicaoDe,
  drenoPorMinuto,
  efeitosDosPapeis,
  eficienciaPressao,
  escolherArquetipo,
  modsOfensivos,
  multiplicadorDefesa,
  papeisDaCpu,
  perfilNeutro,
  pesoFinalizador,
  ORDEM_ESTILO,
  type Arquetipo,
  type CondicaoEquipe,
  type PapelAtivo,
  type PerfilTatico,
} from './taticas';
import type { AjusteIntervalo, Clube, CondicaoJogo, ConcentracaoAtaque, Escalacao, Estilo, Formacao, Funcoes, Jogador, Marcacao, Papel, Passe, PlanoSub, Posicao, Pressao } from './tipos';

/**
 * Motor de partida. Reimplementação própria a partir da especificação em ../../../conhecimento/02-partida.md:
 * a cada minuto um dos times tem "a vez"; sorteia-se a posse (meio-campo), depois ataque x defesa,
 * depois o resultado da finalização (gol, defesa, para fora). Cartões e lesões são sorteios independentes por minuto.
 * Cada titular joga em um "slot" (posição em campo); fora da posição natural ele rende menos (ver tatica.fatorPosicao).
 */

export type TipoEvento = 'gol' | 'defesa' | 'fora' | 'penalti' | 'amarelo' | 'vermelho' | 'lesao' | 'substituicao' | 'tatica';
export type Lado = 0 | 1;
type Par = [number, number];

export interface EventoPartida {
  tempo: 1 | 2;
  /** minuto sequencial da simulação (1..total), usado para reproduzir o jogo ao vivo */
  minuto: number;
  rotulo: string;
  tipo: TipoEvento;
  lado: Lado;
  jogador: string;
  jogadorId: number;
  detalhe?: string;
  /** substituições: quem saiu */
  saiuId?: number;
  /** autor do passe do gol, quando houve */
  assistenteId?: number;
  assistente?: string;
  /** mudança de formação (evento 'tatica'): a formação nova e quem ocupa cada slot dela (null = slot vazio) */
  formacao?: Formacao;
  ordem?: (number | null)[];
}

export interface Estatisticas {
  posse: Par;
  finalizacoes: Par;
  noGol: Par;
  fora: Par;
  desarmes: Par;
  passesErrados: Par;
  amarelos: Par;
  vermelhos: Par;
  lesoes: Par;
}

export interface Participacao {
  jogadorId: number;
  lado: Lado;
  titular: boolean;
  entrou: boolean;
  saiu: boolean;
  gols: number;
  assistencias: number;
  amarelos: number;
  vermelho: boolean;
  /** rodadas fora por lesão sofrida neste jogo (0 se não se lesionou) */
  lesaoRodadas: number;
  /** nota do jogo, de 3,0 a 10,0 */
  nota: number;
  /** energia (0..100) com que o jogador terminou o jogo ou saiu de campo: a partir dela o cansaço passa para a rodada seguinte */
  energiaFim?: number;
}

export interface ResultadoPartida {
  gols: Par;
  eventos: EventoPartida[];
  estat: Estatisticas;
  participacoes: Participacao[];
  /** minutos totais simulados, com acréscimos */
  totalMinutos: number;
  /** minuto sequencial em que termina o 1º tempo */
  fimPrimeiroTempo: number;
  /** quem estava em campo no apito final (sem os expulsos), por lado: base da disputa de pênaltis */
  emCampoFinal: [number[], number[]];
  escalacaoInicial: [EscalacaoInicial, EscalacaoInicial];
  /** energia média (%) dos jogadores de linha em campo, por lado, nos minutos de MINUTOS_FISICO */
  fisico?: [number[], number[]];
}

/** Minutos do relógio em que a energia média do time é registrada (0 = início). */
export const MINUTOS_FISICO = [0, 15, 30, 45, 60, 75, 90] as const;

/**
 * Mudança feita pelo técnico com a partida pausada (modo online). Vale a partir do minuto sequencial `minuto`;
 * tudo o que veio antes fica igual, porque a simulação é determinística.
 */
export interface IntervencaoPartida {
  minuto: number;
  subs: { saiId: number; entraId: number }[];
  estilo?: Estilo;
  marcacao?: Marcacao;
  ataque?: ConcentracaoAtaque;
  passe?: Passe;
  pressao?: Pressao;
  /** nova formação e quem fica em cada slot dela (todos os que estão em campo depois das substituições, uma vez cada) */
  formacao?: { nome: Formacao; ordem: (number | null)[] };
}

export interface OpcoesPartida {
  /** campo neutro: sem vantagem do mandante */
  neutro?: boolean;
  /** divisor da diferença de força; o original usa 8 (11 em níveis altos) */
  divisor?: number;
  /** intervenções dos técnicos humanos durante o jogo, por lado (0 = mandante) */
  intervencoes?: [IntervencaoPartida[], IntervencaoPartida[]];
  /** acréscimo fixo do 1º tempo (online: 0, para todos os jogos pararem juntos no intervalo); ausente = sorteia 0..2 */
  acrescimo1?: number;
}

/** Escalação com que cada lado começou o jogo (ids na ordem dos slots) e o banco. */
export interface EscalacaoInicial {
  titulares: number[];
  slots: Posicao[];
  reservas: number[];
}

/** Quem está em campo e no banco depois de `ate` minutos (replay dos eventos do jogo). */
export interface SituacaoEmCampo {
  /** ocupante de cada slot da escalação inicial (null = o slot ficou sem ninguém) */
  ocupantes: (number | null)[];
  slots: Posicao[];
  banco: number[];
  /** substituições já feitas (por técnico, lesão ou plano) */
  substituicoes: number;
  /** formação em campo agora, quando o técnico mudou durante o jogo (senão a do início: ver `slots`) */
  formacao?: Formacao;
}

export function situacaoEm(r: ResultadoPartida, lado: Lado, ate: number): SituacaoEmCampo {
  const ini = r.escalacaoInicial[lado];
  let ocupantes: (number | null)[] = [...ini.titulares];
  let slots = [...ini.slots];
  let formacao: Formacao | undefined;
  const banco = new Set(ini.reservas);
  const fora = new Set<number>();
  let substituicoes = 0;
  for (const ev of r.eventos) {
    if (ev.minuto > ate || ev.lado !== lado) continue;
    if (ev.tipo === 'tatica' && ev.formacao && ev.ordem) {
      formacao = ev.formacao;
      slots = slotsDaFormacao(ev.formacao);
      ocupantes = [...ev.ordem];
    } else if (ev.tipo === 'vermelho' || ev.tipo === 'lesao') fora.add(ev.jogadorId);
    else if (ev.tipo === 'substituicao') {
      const i = ev.saiuId === undefined ? -1 : ocupantes.indexOf(ev.saiuId);
      if (i >= 0) ocupantes[i] = ev.jogadorId;
      banco.delete(ev.jogadorId);
      substituicoes++;
    }
  }
  return { ocupantes: ocupantes.map((id) => (id === null || fora.has(id) ? null : id)), slots, banco: [...banco], substituicoes, ...(formacao ? { formacao } : {}) };
}

export const MAX_SUBSTITUICOES = 5;

/** Bônus de mando de campo na etapa de finalização (ver calibrar.ts). */
export const VANTAGEM_CASA_FINALIZACAO = 0.05;

/** Efeito da mentalidade sobre ataque e defesa (os demais efeitos táticos estão em taticas.ts). */
export const EFEITO_ESTILO: Record<Estilo, { ataque: number; defesa: number }> = {
  retranca: { ataque: 0.86, defesa: 1.13 },
  defensivo: { ataque: 0.93, defesa: 1.07 },
  equilibrado: { ataque: 1, defesa: 1 },
  ofensivo: { ataque: 1.07, defesa: 0.93 },
  total: { ataque: 1.14, defesa: 0.86 },
};

export { disponivel };

/** Escalação automática 4-4-2 (atalho mantido para testes e scripts). */
export function escalarAuto(clube: Clube): Escalacao {
  return escalarAutoFormacao(clube, '4-4-2');
}

// ---- Estado interno do jogo -------------------------------------------------------------------

interface EstadoLado {
  clube: Clube;
  campo: Jogador[];
  banco: Jogador[];
  /** posição em campo de cada jogador que está jogando */
  slot: Map<number, Posicao>;
  estilo: Estilo;
  marcacao: Marcacao;
  ataque: ConcentracaoAtaque;
  passe: Passe;
  pressao: Pressao;
  gols: number;
  substituicoes: number;
  amarelos: Map<number, number>;
  part: Map<number, Participacao>;
  plano: PlanoSub[];
  /** índices do plano já executados ou descartados */
  planoFeito: Set<number>;
  ajustes: AjusteIntervalo[];
  funcoes: Funcoes;
  /** função individual de cada jogador (só vale com ele em campo na posição da função) */
  papeis: Record<number, Papel>;
  /** energia ao vivo (0..100) de quem está no jogo: cai minuto a minuto conforme a tática */
  en: Map<number, number>;
  /** o técnico é a CPU e escolhe/ajusta a tática sozinho */
  cpu: boolean;
  /** arquétipo em uso pela CPU */
  arq: Arquetipo | null;
  /** condição da equipe (recalculada quando algo muda em campo e a cada bloco de desgaste) */
  cond: CondicaoEquipe;
  /** força do meio-campo na última atualização da condição */
  meio: number;
  /** a condição precisa ser recalculada (substituição, expulsão, mudança tática, desgaste) */
  sujo: boolean;
  /** força base (0..100) de cada jogador, calculada uma vez por jogo */
  bf: Map<number, number>;
  /** aptidão de cada jogador na função que recebeu */
  apt: Map<number, number>;
  /** avisos táticos já dados (cada um aparece uma vez por jogo) */
  avisos: Set<string>;
}

const perfilDe = (l: EstadoLado): PerfilTatico => ({ estilo: l.estilo, passe: l.passe, pressao: l.pressao, marcacao: l.marcacao, ataque: l.ataque });

function preparar(clube: Clube, lado: Lado, esc: Escalacao, cpu: boolean): EstadoLado {
  const part = new Map<number, Participacao>();
  const slot = new Map<number, Posicao>();
  const nova = (j: Jogador, titular: boolean): void => {
    part.set(j.id, { jogadorId: j.id, lado, titular, entrou: titular, saiu: false, gols: 0, assistencias: 0, amarelos: 0, vermelho: false, lesaoRodadas: 0, nota: 6 });
  };
  esc.titulares.forEach((j, i) => {
    nova(j, true);
    slot.set(j.id, esc.slots?.[i] ?? j.posicao);
  });
  esc.reservas.forEach((j) => nova(j, false));
  const en = new Map<number, number>();
  for (const j of [...esc.titulares, ...esc.reservas]) en.set(j.id, j.energia);
  return {
    clube,
    campo: [...esc.titulares],
    banco: [...esc.reservas],
    slot,
    estilo: esc.estilo ?? 'equilibrado',
    marcacao: esc.marcacao ?? 'leve',
    ataque: esc.ataque ?? 'meio',
    passe: esc.passe ?? 'misto',
    pressao: esc.pressao ?? 'media',
    gols: 0,
    substituicoes: 0,
    amarelos: new Map(),
    part,
    plano: esc.plano ?? [],
    planoFeito: new Set(),
    ajustes: esc.ajustes ?? [],
    funcoes: esc.funcoes ?? {},
    papeis: { ...(esc.funcoes?.papeis ?? {}) },
    en,
    cpu,
    arq: null,
    cond: condicaoDaEscalacao(esc),
    meio: 0,
    sujo: true,
    bf: new Map(),
    apt: new Map(),
    avisos: new Set(),
  };
}

function inicial(e: Escalacao): EscalacaoInicial {
  return { titulares: e.titulares.map((j) => j.id), slots: e.titulares.map((j, i) => e.slots?.[i] ?? j.posicao), reservas: e.reservas.map((j) => j.id) };
}

/** O placar do ponto de vista de `l` bate com a condição pedida? */
function condicaoVale(c: CondicaoJogo, golsPro: number, golsContra: number): boolean {
  if (c === 'sempre') return true;
  if (c === 'perdendo') return golsPro < golsContra;
  if (c === 'ganhando') return golsPro > golsContra;
  return golsPro === golsContra;
}

/** Efeito da agressividade da marcação nos cartões (o efeito dela na defesa está em taticas.multiplicadorDefesa). */
const EFEITO_MARCACAO: Record<Marcacao, { defesa: number; cartoes: number }> = {
  leve: { defesa: 0.985, cartoes: 0.8 },
  normal: { defesa: 1, cartoes: 1 },
  forte: { defesa: 1.03, cartoes: 1.4 },
};

const posDe = (l: EstadoLado, j: Jogador): Posicao => l.slot.get(j.id) ?? j.posicao;
/** Força do jogador no slot em que joga, com a energia que ele tem NESTE minuto do jogo. */
const baseDe = (l: EstadoLado, j: Jogador): number => {
  let b = l.bf.get(j.id);
  if (b === undefined) {
    b = modoHabilidadeLigado() ? forcaPorHabilidade(j) : j.overall;
    l.bf.set(j.id, b);
  }
  return b;
};
const forcaNoSlot = (l: EstadoLado, j: Jogador): number => {
  const en = l.en.get(j.id) ?? j.energia;
  // a energia tira até 25% da força; abaixo de 80% a perna pesa de verdade (desconto extra)
  return ((baseDe(l, j) * (0.75 + 0.25 * (en / 100))) / 10) * (en < 80 ? 1 - 0.0045 * (80 - en) : 1) * fatorPosicao(j.posicao, posDe(l, j));
};

function topForcas(l: EstadoLado, posicoes: Posicao[], max: number): number[] {
  return l.campo
    .filter((j) => posicoes.includes(posDe(l, j)))
    .map((j) => forcaNoSlot(l, j))
    .sort((a, b) => b - a)
    .slice(0, max);
}

/**
 * Como no original, as linhas somam a força dos melhores N jogadores e dividem por N fixo (ver tatica.LINHAS):
 * formação com menos gente numa linha rende menos nela, e perder um jogador por cartão ou lesão pesa de verdade.
 */
const LINHA_MEIO = LINHAS.meio;
const LINHA_ATAQUE = LINHAS.ataque;
const LINHA_DEFESA = LINHAS.defesa;
const somaLinha = (v: number[], n: number): number => (v.length ? Math.max(0.1, v.reduce((s, x) => s + x, 0) / n) : 0.1);

/** Falso 9: o atacante com a função (ou, na opção antiga, o primeiro) recua para o meio-campo; só vale com 2 ou mais atacantes. */
function falsoNove(l: EstadoLado): Jogador | undefined {
  const atacantes = l.campo.filter((j) => posDe(l, j) === 'A');
  if (atacantes.length < 2) return undefined;
  const designado = atacantes.find((j) => l.papeis[j.id] === 'falso9');
  if (designado) return designado;
  return l.funcoes.falso9 ? atacantes[0] : undefined;
}

/** Funções individuais em vigor: o jogador tem que estar em campo na posição da função (se saiu, a função some com ele). */
function papeisAtivos(l: EstadoLado): PapelAtivo[] {
  const out: PapelAtivo[] = [];
  for (const j of l.campo) {
    const papel = l.papeis[j.id];
    if (papel && PAPEIS[papel].posicao === posDe(l, j)) {
      let apt = l.apt.get(j.id);
      if (apt === undefined) {
        apt = aptidaoPapel(papel, j);
        l.apt.set(j.id, apt);
      }
      out.push({ papel, jogador: j, aptidao: apt });
    }
  }
  return out;
}

/** Função do jogador, se ele a cumpre (em campo, na posição dela). */
function papelValido(l: EstadoLado, j: Jogador): Papel | undefined {
  const papel = l.papeis[j.id];
  return papel && PAPEIS[papel].posicao === posDe(l, j) ? papel : undefined;
}

/** Recalcula a condição da equipe (forma física, meio-campo, velocidade, jogo aéreo e funções) com o que há em campo agora. */
function atualizarCondicao(l: EstadoLado): void {
  l.meio = forcaMeioBruta(l);
  l.cond = condicaoDe(l.campo, (j) => posDe(l, j), (j) => l.en.get(j.id) ?? j.energia, l.meio, efeitosDosPapeis(papeisAtivos(l), l.ataque, l.passe));
  l.sujo = false;
}

/** Recalcula a condição só se algo mudou desde a última vez. */
function condicaoAtual(l: EstadoLado): void {
  if (l.sujo) atualizarCondicao(l);
}

const forcaMeioBruta = (l: EstadoLado): number => {
  const forcas = topForcas(l, ['M'], LINHA_MEIO + 1);
  const f9 = falsoNove(l);
  if (f9) forcas.push(forcaNoSlot(l, f9));
  // lateral invertido ajuda no meio-campo
  for (const j of l.campo) if (l.papeis[j.id] === 'lateral-invertido' && posDe(l, j) === 'L') forcas.push(forcaNoSlot(l, j) * 0.85);
  return somaLinha(forcas.sort((a, b) => b - a).slice(0, LINHA_MEIO), LINHA_MEIO);
};
const forcaMeio = (l: EstadoLado): number => l.meio;
/** Força do ataque sem os efeitos de mentalidade, passe e pressão (esses entram por taticas.modsOfensivos). */
const forcaAtaque = (l: EstadoLado): number => {
  const f9 = falsoNove(l);
  const lista = f9
    ? l.campo.filter((j) => posDe(l, j) === 'A' && j.id !== f9.id).map((j) => forcaNoSlot(l, j)).sort((a, b) => b - a).slice(0, LINHA_ATAQUE)
    : topForcas(l, ['A'], LINHA_ATAQUE);
  let f = somaLinha(lista, LINHA_ATAQUE);
  if (l.ataque === 'lados') f *= l.campo.filter((j) => posDe(l, j) === 'L').length >= 2 ? 1.02 : 0.98;
  return f;
};
const forcaDefesa = (l: EstadoLado): number => somaLinha(topForcas(l, ['Z', 'L'], LINHA_DEFESA), LINHA_DEFESA) * multiplicadorDefesa(perfilDe(l), l.cond);
const forcaGoleiro = (l: EstadoLado): number => {
  const g = l.campo.find((j) => posDe(l, j) === 'G');
  return g ? forcaNoSlot(l, g) : 0.1;
};

// ---- Constantes de sorteio (ver doc 02) -------------------------------------------------------

const PESOS_FINALIZACAO_BASE: [number, number, number] = [5.5, 35.55, 15];
const PESOS_FINALIZACAO_3_GOLS: [number, number, number] = [4.5, 40.55, 15];
const PESOS_FINALIZACAO_5_GOLS: [number, number, number] = [3, 40.55, 15];
const PESOS_FINALIZACAO_6_GOLS: [number, number, number] = [0.5, 40.55, 15];

const PESO_BATEDOR: Record<Posicao, number> = { G: 0, Z: 1, L: 1, M: 6, A: 22 };
const PESO_AMARELO: Record<Posicao, number> = { G: 1, Z: 4, L: 3, M: 4, A: 3 };

const N_AMARELO: [number[], number[]] = [
  [70, 40, 30],
  [45, 40, 30],
];
const N_VERMELHO: [number[], number[]] = [
  [1200, 900, 800],
  [800, 700, 550],
];
const N_LESAO: [number[], number[]] = [
  [1500, 1000, 800],
  [800, 600, 600],
];

/**
 * Fração das defesas do goleiro e das bolas para fora que, na tela ao vivo, são pênaltis perdidos. Calibrados com a fração de
 * gols de pênalti abaixo para que cerca de 3 em cada 4 pênaltis sejam convertidos (como no futebol real): antes, as perdas
 * (4% das defesas + 1,5% das bolas fora) superavam os gols de pênalti (5% dos gols) e a maioria dos pênaltis era perdida.
 */
const CHANCE_PENALTI_DEFENDIDO = 0.008;
const CHANCE_PENALTI_FORA = 0.003;

const TIPOS_GOL =['normal', 'pênalti', 'falta', 'contra', 'olímpico'] as const;

function tipoDeGol(rng: Rng): (typeof TIPOS_GOL)[number] {
  const r = rng.inteiro(1000);
  if (r < 850) return 'normal';
  if (r < 940) return 'pênalti';
  if (r < 975) return 'falta';
  if (r < 985) return 'contra';
  if (r < 990) return 'olímpico';
  return 'normal';
}

// ---- Simulação --------------------------------------------------------------------------------

export function simularPartida(
  mandante: Clube,
  visitante: Clube,
  rng: Rng,
  opcoes: OpcoesPartida = {},
  escalacoes?: [Escalacao, Escalacao],
): ResultadoPartida {
  const neutro = opcoes.neutro ?? false;
  const divisor = opcoes.divisor ?? 8;
  const esc = escalacoes ?? [resolverEscalacao(mandante).escalacao, resolverEscalacao(visitante).escalacao];
  // a CPU escolhe a tática sozinha (por adversário) quando a escalação não foi dada de fora; com escalação explícita vale ela
  const cpuLado: [boolean, boolean] = [!escalacoes && !mandante.humano, !escalacoes && !visitante.humano];
  const lados: [EstadoLado, EstadoLado] = [preparar(mandante, 0, esc[0], cpuLado[0]), preparar(visitante, 1, esc[1], cpuLado[1])];

  const eventos: EventoPartida[] = [];
  const estat: Estatisticas = {
    posse: [0, 0],
    finalizacoes: [0, 0],
    noGol: [0, 0],
    fora: [0, 0],
    desarmes: [0, 0],
    passesErrados: [0, 0],
    amarelos: [0, 0],
    vermelhos: [0, 0],
    lesoes: [0, 0],
  };

  const sorteioAcr1 = rng.inteiro(3); // 0..2 (sempre sorteado, para o resto da sequência não mudar)
  const acr1 = opcoes.acrescimo1 ?? sorteioAcr1;
  const acr2 = 1 + rng.inteiro(5); // 1..5
  const fim1 = 45 + acr1;
  const total = fim1 + 45 + acr2;
  let totalAmarelos = 0;
  let vez: Lado = rng.inteiro(2) as Lado;
  // sorteio próprio da CPU (não consome o da partida): mesma partida, mesmas escolhas, em qualquer reprodução
  const rngCpu = new Rng((rng.estado ^ Math.imul(mandante.id + 1, 2654435761) ^ Math.imul(visitante.id + 7, 40503)) >>> 0);

  const rotulo = (t: number): string => {
    if (t <= 45) return String(t);
    if (t <= fim1) return `45+${t - 45}`;
    const m = t - acr1;
    return m <= 90 ? String(m) : `90+${m - 90}`;
  };

  const registrar = (tempo: 1 | 2, t: number, tipo: TipoEvento, lado: Lado, j: Jogador, detalhe?: string): EventoPartida => {
    const ev: EventoPartida = { tempo, minuto: t, rotulo: rotulo(t), tipo, lado, jogador: j.nome, jogadorId: j.id };
    if (detalhe) ev.detalhe = detalhe;
    eventos.push(ev);
    return ev;
  };

  const sair = (l: EstadoLado, j: Jogador): void => {
    l.campo = l.campo.filter((x) => x.id !== j.id);
    l.sujo = true;
    const p = l.part.get(j.id);
    if (p) p.saiu = true;
  };

  /** Troca o jogador que saiu por um reserva; o substituto assume o slot de quem saiu. */
  const substituir = (l: EstadoLado, lado: Lado, saiu: Jogador, slot: Posicao, tempo: 1 | 2, t: number): void => {
    if (l.substituicoes >= MAX_SUBSTITUICOES || l.banco.length === 0) return;
    let indice = -1;
    let melhorNota = -1;
    // prefere quem joga na mesma posição de quem saiu; sem ninguém assim, cai no melhor adaptado (goleiro só por goleiro)
    l.banco.forEach((j, i) => {
      if ((j.posicao === 'G') !== (slot === 'G')) return;
      const nota = forcaBase(j) * fatorPosicao(j.posicao, slot) + (j.posicao === slot ? 1000 : 0);
      if (nota > melhorNota) {
        melhorNota = nota;
        indice = i;
      }
    });
    if (indice < 0) return;
    const entra = l.banco.splice(indice, 1)[0] as Jogador;
    l.campo.push(entra);
    l.sujo = true;
    l.slot.set(entra.id, slot);
    l.substituicoes++;
    const p = l.part.get(entra.id);
    if (p) p.entrou = true;
    const evSub = registrar(tempo, t, 'substituicao', lado, entra, `entra no lugar de ${saiu.nome}`);
    evSub.saiuId = saiu.id;
  };

  /** Minuto sequencial da simulação em que vale um minuto do relógio do plano (0 = intervalo). */
  const tDoPlano = (minuto: number): number => (minuto <= 0 ? fim1 + 1 : minuto <= 45 ? minuto : minuto + acr1);

  /** Troca `saiu` por `entra` (que está no banco), no slot de quem saiu. */
  const trocar = (l: EstadoLado, lado: Lado, saiu: Jogador, entra: Jogador, tempo: 1 | 2, t: number): void => {
    const slot = posDe(l, saiu);
    sair(l, saiu);
    l.banco = l.banco.filter((x) => x.id !== entra.id);
    l.campo.push(entra);
    l.sujo = true;
    l.slot.set(entra.id, slot);
    l.substituicoes++;
    const p = l.part.get(entra.id);
    if (p) p.entrou = true;
    const ev = registrar(tempo, t, 'substituicao', lado, entra, `entra no lugar de ${saiu.nome}`);
    ev.saiuId = saiu.id;
  };

  /** Aplica a mudança tática pedida e devolve o texto do que mudou de fato. */
  const mudarTatica = (l: EstadoLado, m: { estilo?: Estilo; marcacao?: Marcacao; ataque?: ConcentracaoAtaque; passe?: Passe; pressao?: Pressao }): string[] => {
    const partes: string[] = [];
    if (m.estilo && m.estilo !== l.estilo) {
      l.estilo = m.estilo;
      partes.push(`estilo ${m.estilo}`);
    }
    if (m.marcacao && m.marcacao !== l.marcacao) {
      l.marcacao = m.marcacao;
      partes.push(`marcação ${m.marcacao}`);
    }
    if (m.ataque && m.ataque !== l.ataque) {
      l.ataque = m.ataque;
      partes.push(`ataques ${m.ataque === 'meio' ? 'pelo meio' : m.ataque === 'lados' ? 'pelos lados' : 'equilibrados'}`);
    }
    if (m.passe && m.passe !== l.passe) {
      l.passe = m.passe;
      partes.push(`passe ${m.passe}`);
    }
    if (m.pressao && m.pressao !== l.pressao) {
      l.pressao = m.pressao;
      partes.push(`pressão ${m.pressao === 'media' ? 'média' : m.pressao}`);
    }
    if (partes.length) l.sujo = true;
    return partes;
  };

  /** Minuto do relógio (1..90+) correspondente ao minuto sequencial `t` da simulação. */
  const minutoDoRelogio = (t: number): number => (t <= 45 ? t : t <= fim1 ? 45 : t - acr1);

  /** Executa as intervenções do técnico (partida pausada) e depois o plano de substituições e os ajustes do intervalo dos dois lados. */
  const aplicarPlano = (t: number, tempo: 1 | 2): void => {
    for (const lado of [0, 1] as const) {
      const l = lados[lado];
      const o = lados[lado === 0 ? 1 : 0];
      // intervenções do técnico com o jogo pausado (online): valem a partir deste minuto
      for (const iv of (opcoes.intervencoes?.[lado] ?? []).filter((x) => x.minuto === t)) {
        for (const s of iv.subs) {
          const saiu = l.campo.find((j) => j.id === s.saiId);
          const entra = l.banco.find((j) => j.id === s.entraId);
          if (!saiu || !entra || l.substituicoes >= MAX_SUBSTITUICOES) continue;
          if ((posDe(l, saiu) === 'G') !== (entra.posicao === 'G')) continue;
          trocar(l, lado, saiu, entra, tempo, t);
        }
        if (iv.formacao) {
          const { nome, ordem } = iv.formacao;
          const slotsNovos = slotsDaFormacao(nome);
          const ids = ordem.filter((x): x is number => x !== null);
          // só vale se todos os que estão em campo aparecem uma vez, o goleiro continua no gol e ninguém de fora entra
          const valida =
            ordem.length === slotsNovos.length &&
            ids.length === l.campo.length &&
            new Set(ids).size === ids.length &&
            ids.every((id) => l.campo.some((j) => j.id === id)) &&
            slotsNovos.every((s, i) => {
              const id = ordem[i];
              const j = id === null || id === undefined ? undefined : l.campo.find((x) => x.id === id);
              return s === 'G' ? j === undefined ? !l.campo.some((x) => posDe(l, x) === 'G') : posDe(l, j) === 'G' : j === undefined || posDe(l, j) !== 'G';
            });
          if (valida) {
            l.sujo = true;
            slotsNovos.forEach((s, i) => {
              const id = ordem[i];
              if (id !== null && id !== undefined) l.slot.set(id, s);
            });
            const ev: EventoPartida = { tempo, minuto: t, rotulo: rotulo(t), tipo: 'tatica', lado, jogador: l.clube.nome, jogadorId: 0, detalhe: `muda a formação: ${nome}`, formacao: nome, ordem: [...ordem] };
            eventos.push(ev);
          }
        }
        const partes = mudarTatica(l, iv);
        if (partes.length) eventos.push({ tempo, minuto: t, rotulo: rotulo(t), tipo: 'tatica', lado, jogador: l.clube.nome, jogadorId: 0, detalhe: `muda a tática: ${partes.join(', ')}` });
      }
      if (t === fim1 + 1) {
        const aj = l.ajustes.find((a) => condicaoVale(a.condicao, l.gols, o.gols));
        if (aj) {
          const partes = mudarTatica(l, aj);
          if (partes.length) eventos.push({ tempo, minuto: t, rotulo: rotulo(t), tipo: 'tatica', lado, jogador: l.clube.nome, jogadorId: 0, detalhe: `muda no intervalo: ${partes.join(', ')}` });
        }
      }
      l.plano.forEach((item, i) => {
        if (l.planoFeito.has(i) || tDoPlano(item.minuto) !== t) return;
        l.planoFeito.add(i);
        if (!condicaoVale(item.condicao, l.gols, o.gols) || l.substituicoes >= MAX_SUBSTITUICOES) return;
        const saiu = l.campo.find((j) => j.id === item.saiId);
        const entra = l.banco.find((j) => j.id === item.entraId);
        if (!saiu || !entra) return; // já saiu (lesão, expulsão) ou já entrou
        if ((posDe(l, saiu) === 'G') !== (entra.posicao === 'G')) return; // goleiro só troca com goleiro
        trocar(l, lado, saiu, entra, tempo, t);
      });
    }
  };

  /** Técnico da CPU: no 2º tempo tira quem está muito cansado, até o limite de substituições. */
  const substituicoesDaCpu = (t: number, tempo: 1 | 2): void => {
    if (tempo !== 2 || t - fim1 < 12) return;
    for (const lado of [0, 1] as const) {
      const l = lados[lado];
      if (l.clube.humano || l.substituicoes >= MAX_SUBSTITUICOES || l.banco.length === 0 || !rng.chance(0.14)) continue;
      // olha a energia que o jogador tem agora (a pressão alta e a marcação forte cansam o time mais cedo)
      const energiaDe = (j: Jogador): number => l.en.get(j.id) ?? j.energia;
      const cansado = [...l.campo].filter((j) => posDe(l, j) !== 'G' && energiaDe(j) < 78).sort((a, b) => energiaDe(a) - energiaDe(b))[0];
      if (!cansado) continue;
      const slot = posDe(l, cansado);
      const melhor = [...l.banco].filter((j) => j.posicao !== 'G').sort((a, b) => forcaBase(b) * fatorPosicao(b.posicao, slot) - forcaBase(a) * fatorPosicao(a.posicao, slot))[0];
      if (melhor) trocar(l, lado, cansado, melhor, tempo, t);
    }
  };

  // ---- Técnico da CPU: escolhe o jeito de jogar contra o adversário e ajusta durante o jogo -------------------

  const forcaMediaEmCampo = (l: EstadoLado): number => {
    const v = l.campo.map((j) => forcaComEnergia(j, l.en.get(j.id) ?? j.energia)).sort((a, b) => b - a).slice(0, 11);
    return v.length ? v.reduce((s, x) => s + x, 0) / v.length : 0;
  };

  const decidirCpu = (lado: Lado, t: number, inicio: boolean): Arquetipo => {
    const l = lados[lado];
    const o = lados[(1 - lado) as Lado];
    // Antes do jogo a CPU só sabe o que a análise do adversário mostra: em 40% das vezes o "olheiro" acerta o jeito de jogar
    // dele (um humano é lido pela tática escolhida); nas demais, supõe o jeito habitual do clube, que pode estar errado.
    // Durante o jogo ela vê o que está acontecendo em campo, mas com 40% de chance de ler errado.
    const leitura = rngCpu.proximo();
    let adversario: PerfilTatico;
    if (inicio) adversario = !o.cpu && leitura < 0.4 ? perfilDe(o) : arquetipoHabitual(o.clube);
    else adversario = leitura < 0.6 ? perfilDe(o) : perfilNeutro();
    return escolherArquetipo(
      l.clube,
      l.cond,
      o.cond,
      { adversario, diferencaForca: forcaMediaEmCampo(l) - forcaMediaEmCampo(o), casa: lado === 0 && !neutro, saldo: l.gols - o.gols, minuto: inicio ? 0 : minutoDoRelogio(t), atualId: l.arq?.id },
      () => (inicio ? (rngCpu.proximo() - 0.5) * 0.12 : 0),
    );
  };

  const adotarArquetipo = (lado: Lado, a: Arquetipo, t: number, inicio: boolean): void => {
    const l = lados[lado];
    const antes = perfilDe(l);
    const partes = mudarTatica(l, a);
    l.arq = a;
    const campoAtual = { titulares: l.campo, reservas: [] as Jogador[], slots: l.campo.map((j) => posDe(l, j)) };
    l.papeis = papeisDaCpu(campoAtual, a);
    l.sujo = true;
    if (inicio) {
      eventos.push({ tempo: 1, minuto: 1, rotulo: '1', tipo: 'tatica', lado, jogador: l.clube.nome, jogadorId: 0, detalhe: `entra em campo apostando em ${a.nome}` });
    } else if (partes.length) {
      eventos.push({ tempo: t <= fim1 ? 1 : 2, minuto: t, rotulo: rotulo(t), tipo: 'tatica', lado, jogador: l.clube.nome, jogadorId: 0, detalhe: `muda a tática (${a.nome}): ${partes.join(', ')}` });
    }
    void antes;
  };

  const MINUTOS_CPU = [fim1 + 1, fim1 + 22, fim1 + 38];
  const ajustesDaCpu = (t: number): void => {
    if (!cpuLado[0] && !cpuLado[1]) return;
    if (t === 1) {
      for (const lado of [0, 1] as const) {
        if (!cpuLado[lado]) continue;
        atualizarCondicao(lados[lado]);
      }
      for (const lado of [0, 1] as const) if (cpuLado[lado]) adotarArquetipo(lado, decidirCpu(lado, t, true), t, true);
      return;
    }
    if (!MINUTOS_CPU.includes(t)) return;
    for (const lado of [0, 1] as const) {
      if (!cpuLado[lado]) continue;
      atualizarCondicao(lados[lado]);
      atualizarCondicao(lados[(1 - lado) as Lado]);
      const a = decidirCpu(lado, t, false);
      if (a.id !== lados[lado].arq?.id) adotarArquetipo(lado, a, t, false);
    }
  };

  // ---- Leitura tática: o que o motor está fazendo com as escolhas dos técnicos, em poucas linhas ----------------

  const ditos = new Set<string>();
  const observar = (t: number): void => {
    const dizer = (chave: string, quem: Lado, texto: string): void => {
      if (ditos.has(chave)) return;
      ditos.add(chave);
      eventos.push({ tempo: t <= fim1 ? 1 : 2, minuto: t, rotulo: rotulo(t), tipo: 'tatica', lado: quem, jogador: lados[quem].clube.nome, jogadorId: 0, detalhe: texto });
    };
    const totalPosse = estat.posse[0] + estat.posse[1];
    for (const lado of [0, 1] as const) {
      const a = lados[lado];
      const b = lados[(1 - lado) as Lado];
      const ob = (1 - lado) as Lado;
      const peA = eficienciaPressao(a.cond.forma);
      const peB = eficienciaPressao(b.cond.forma);
      if (b.pressao === 'alta' && peB >= 0.75 && a.passe === 'curto' && a.cond.meio < 6.4) dizer(`sufoca${ob}`, ob, `${b.clube.nome} pressiona alto e sufoca a saída de bola do ${a.clube.nome}`);
      if (a.passe === 'longo' && b.pressao === 'alta' && a.cond.vel >= 0.6) dizer(`costas${lado}`, lado, `${a.clube.nome} acha bolas nas costas da defesa adiantada do ${b.clube.nome}`);
      if (a.passe === 'curto' && b.pressao === 'baixa' && totalPosse > 0 && estat.posse[lado] / totalPosse >= 0.56) dizer(`fechado${lado}`, ob, `${a.clube.nome} tem a bola, mas o ${b.clube.nome} fecha os espaços no bloco baixo`);
      if (ORDEM_ESTILO[a.estilo] <= 0 && (ORDEM_ESTILO[b.estilo] >= 1 || b.pressao === 'alta') && a.cond.vel >= 0.62) dizer(`contra${lado}`, lado, `${a.clube.nome} espera e sai em velocidade: o ${b.clube.nome} deixa espaços atrás`);
      if (a.passe === 'longo' && b.pressao === 'baixa' && a.cond.aereoAtq >= 0.65) dizer(`aereo${lado}`, lado, `${a.clube.nome} leva vantagem no jogo aéreo contra o bloco baixo do ${b.clube.nome}`);
      if ((a.pressao === 'alta' || a.estilo === 'total') && a.cond.forma < 0.78 && peA < 1) dizer(`cansaco${lado}`, lado, `${a.clube.nome} sente o desgaste da pressão: o time cansa e a defesa abre espaços`);
      else if (a.cond.forma < 0.66) dizer(`exausto${lado}`, lado, `${a.clube.nome} está exausto: as pernas pesam`);
    }
  };

  const disciplina = (t: number, tempo: 1 | 2): void => {
    const minutoNoTempo = tempo === 1 ? t : t - fim1;
    const fase = minutoNoTempo < 15 ? 0 : minutoNoTempo < 30 ? 1 : 2;
    const idx = tempo - 1;
    const lado = rng.inteiro(2) as Lado;
    const l = lados[lado];
    if (l.campo.length === 0) return;

    let nAmarelo = (N_AMARELO[idx] as number[])[fase] as number;
    if (totalAmarelos > 5) nAmarelo *= 2;
    nAmarelo = Math.max(1, Math.round(nAmarelo / EFEITO_MARCACAO[l.marcacao].cartoes));
    const nVermelho = (N_VERMELHO[idx] as number[])[fase] as number;
    const nLesao = (N_LESAO[idx] as number[])[fase] as number;

    if (rng.inteiro(nAmarelo) === 0) {
      const alvo = l.campo[rng.ponderado(l.campo.map((j) => PESO_AMARELO[posDe(l, j)]))] as Jogador;
      const antes = l.amarelos.get(alvo.id) ?? 0;
      const p = l.part.get(alvo.id) as Participacao;
      totalAmarelos++;
      estat.amarelos[lado]++;
      p.amarelos++;
      l.amarelos.set(alvo.id, antes + 1);
      if (antes >= 1) {
        registrar(tempo, t, 'vermelho', lado, alvo, 'segundo amarelo');
        estat.vermelhos[lado]++;
        p.vermelho = true;
        sair(l, alvo);
      } else {
        registrar(tempo, t, 'amarelo', lado, alvo);
      }
    } else if (rng.inteiro(nVermelho) === 0) {
      const alvo = l.campo[rng.inteiro(l.campo.length)] as Jogador;
      registrar(tempo, t, 'vermelho', lado, alvo, 'vermelho direto');
      estat.vermelhos[lado]++;
      (l.part.get(alvo.id) as Participacao).vermelho = true;
      sair(l, alvo);
    } else if (rng.inteiro(nLesao) === 0) {
      const alvo = l.campo[rng.inteiro(l.campo.length)] as Jogador;
      const slotDele = posDe(l, alvo);
      const dias = [1, 2, 3, 4, 5, 6][rng.ponderado([30, 25, 15, 12, 10, 8])] as number;
      registrar(tempo, t, 'lesao', lado, alvo, `fora por ${dias} rodada${dias > 1 ? 's' : ''}`);
      estat.lesoes[lado]++;
      (l.part.get(alvo.id) as Participacao).lesaoRodadas = dias;
      sair(l, alvo);
      substituir(l, lado, alvo, slotDele, tempo, t);
    }
  };

  const turno = (ladoVez: Lado, tempo: 1 | 2, t: number): void => {
    const A = lados[ladoVez];
    const outro = (1 - ladoVez) as Lado;
    const B = lados[outro];
    const casa = !neutro && ladoVez === 0;

    // 1) posse (o jeito de jogar de cada um e o confronto entre os dois estilos mexem no peso de cada lado)
    const mA = forcaMeio(A);
    const mB = forcaMeio(B);
    const perfilA = perfilDe(A);
    const perfilB = perfilDe(B);
    const modA = modsOfensivos(perfilA, A.cond, perfilB, B.cond);
    const modB = modsOfensivos(perfilB, B.cond, perfilA, A.cond);
    const pA = Math.max(0.2, 1 + (mA - mB) / divisor + (casa ? 0.3 : 0));
    const pB = Math.max(0.2, 1 + (mB - mA) / divisor);
    const bonusMeio = (l: EstadoLado): number => (l.ataque === 'meio' ? 1.03 : 1);
    const comPosse = rng.ponderado([55 * pA * bonusMeio(A) * modA.posse, 45 * pB * bonusMeio(B) * modB.posse]) === 0 ? ladoVez : outro;
    estat.posse[comPosse]++;

    if (comPosse !== ladoVez) {
      // o adversário recuperou: desarme dele ou passe errado de quem tinha a vez
      if (rng.chance(0.5)) estat.desarmes[outro]++;
      else estat.passesErrados[ladoVez]++;
      return;
    }

    // 2) ataque x defesa
    const at = forcaAtaque(A) * modA.ataque;
    const df = forcaDefesa(B);
    const wa = Math.max(0.2, 1 + (at - df) / divisor + (casa ? 0.3 : 0));
    const wd = Math.max(0.2, 1 + (df - at) / divisor);
    if (rng.ponderado([50 * wa, 50 * wd]) !== 0) {
      if (rng.chance(0.5)) estat.desarmes[outro]++;
      else estat.passesErrados[ladoVez]++;
      return;
    }

    // 3) finalização
    // quem tem a característica Finalização (código 9) é escolhido com mais frequência para chutar
    const batedor = A.campo[rng.ponderado(A.campo.map((j) => PESO_BATEDOR[posDe(A, j)] * (j.caracteristicas.includes(9) ? 1.5 : 1) * pesoFinalizador(papelValido(A, j))))] as Jogador;
    estat.finalizacoes[ladoVez]++;
    const gk = forcaGoleiro(B);
    const div2 = divisor >= 11 ? 10 : 8;
    let fDefesa = 1 + (gk - forcaNoSlot(A, batedor)) / div2;
    let fFora = 1 + (df - at) / div2;
    // Desvio deliberado do original: lá o mandante ganhava +0,1 nos pesos de defesa e de bola fora (o que o prejudicava);
    // aqui a vantagem de casa reduz o peso de defesa/fora do adversário quando o mandante ataca, e o aumenta quando ele defende.
    if (!neutro) {
      const ajuste = ladoVez === 0 ? -VANTAGEM_CASA_FINALIZACAO : VANTAGEM_CASA_FINALIZACAO;
      fDefesa += ajuste;
      fFora += ajuste;
    }
    fDefesa = Math.max(0.2, fDefesa);
    fFora = Math.max(0.2, fFora);

    let base = PESOS_FINALIZACAO_BASE;
    if (A.gols >= 6) base = PESOS_FINALIZACAO_6_GOLS;
    else if (A.gols >= 5) base = PESOS_FINALIZACAO_5_GOLS;
    else if (A.gols >= 3) base = PESOS_FINALIZACAO_3_GOLS;
    if (A.gols >= 2 && B.clube.reputacao - A.clube.reputacao >= 2) base = PESOS_FINALIZACAO_5_GOLS;

    const r = rng.ponderado([base[0], base[1] * fDefesa, base[2] * fFora]);
    // pênalti e falta são cobrados por quem o técnico designou, se ele estiver em campo
    const cobrador = (id: number | null | undefined, padrao: Jogador): Jogador => (id ? A.campo.find((j) => j.id === id) ?? padrao : padrao);
    if (r === 0) {
      const tipo = tipoDeGol(rng);
      const autor = tipo === 'pênalti' ? cobrador(A.funcoes.batedorPenaltiId, batedor) : tipo === 'falta' ? cobrador(A.funcoes.batedorFaltaId, batedor) : batedor;
      A.gols++;
      (A.part.get(autor.id) as Participacao).gols++;
      estat.noGol[ladoVez]++;
      const outros = A.campo.filter((j) => j.id !== autor.id && posDe(A, j) !== 'G');
      // o pênalti é marcado antes do gol (a tela ao vivo faz o suspense entre os dois)
      if (tipo === 'pênalti') registrar(tempo, t, 'penalti', ladoVez, autor, 'gol');
      const ev = registrar(tempo, t, 'gol', ladoVez, autor, tipo);
      if (tipo === 'normal' && outros.length > 0 && rng.chance(0.7)) {
        // Passe (11) e Armação (4) aumentam a chance de dar a assistência; o batedor de escanteios também
        const garcom = outros[rng.ponderado(outros.map((j) => (j.caracteristicas.some((c) => c === 11 || c === 4) ? 2 : 1) * (j.id === A.funcoes.batedorEscanteioId ? 2 : 1)))] as Jogador;
        ev.assistenteId = garcom.id;
        ev.assistente = garcom.nome;
        (A.part.get(garcom.id) as Participacao).assistencias++;
      }
    } else if (r === 1) {
      estat.noGol[ladoVez]++;
      const goleiro = B.campo.find((j) => posDe(B, j) === 'G');
      // parte das defesas é pênalti defendido (não muda o placar nem as estatísticas)
      if (rng.chance(CHANCE_PENALTI_DEFENDIDO)) registrar(tempo, t, 'penalti', ladoVez, cobrador(A.funcoes.batedorPenaltiId, batedor), 'defendido');
      else if (goleiro) registrar(tempo, t, 'defesa', outro, goleiro, `finalização de ${batedor.nome}`);
    } else {
      estat.fora[ladoVez]++;
      if (rng.chance(CHANCE_PENALTI_FORA)) registrar(tempo, t, 'penalti', ladoVez, cobrador(A.funcoes.batedorPenaltiId, batedor), 'fora');
      else registrar(tempo, t, 'fora', ladoVez, batedor);
    }
  };

  // energia média (%) dos jogadores de linha, por lado, em cada MINUTOS_FISICO
  const fisico: [number[], number[]] = [[], []];
  const instantesFisico = [0, 15, 30, 45, fim1 + 15, fim1 + 30, fim1 + 45];
  const registrarFisico = (): void => {
    for (const lado of [0, 1] as const) {
      const l = lados[lado];
      const linha = l.campo.filter((j) => posDe(l, j) !== 'G');
      fisico[lado].push(linha.length ? Math.round(linha.reduce((s, j) => s + (l.en.get(j.id) ?? j.energia), 0) / linha.length) : 0);
    }
  };
  registrarFisico();

  // desgaste em blocos de 3 minutos: pressão, mentalidade, agressividade, passe, posição, função, idade e resistência
  let semDreno = 0;
  const drenar = (minutos: number): void => {
    for (const l of lados) {
      const perfil = perfilDe(l);
      for (const j of l.campo) l.en.set(j.id, Math.max(20, (l.en.get(j.id) ?? j.energia) - minutos * drenoPorMinuto(perfil, j, posDe(l, j), papelValido(l, j))));
      l.sujo = true;
    }
  };

  for (let t = 1; t <= total; t++) {
    const tempo: 1 | 2 = t <= fim1 ? 1 : 2;
    aplicarPlano(t, tempo);
    // a CPU decide e a leitura tática comenta com a condição de cada equipe em dia
    if (t === 1 || MINUTOS_CPU.includes(t) || t === 20 || t === 40 || t === fim1 + 20 || t === fim1 + 38) {
      condicaoAtual(lados[0]);
      condicaoAtual(lados[1]);
      ajustesDaCpu(t);
      if (t === 20 || t === 40 || t === fim1 + 20 || t === fim1 + 38) observar(t);
    }
    substituicoesDaCpu(t, tempo);
    disciplina(t, tempo);
    condicaoAtual(lados[0]);
    condicaoAtual(lados[1]);
    turno(vez, tempo, t);
    semDreno++;
    if (semDreno >= 3 || t === total || t === fim1 || instantesFisico.includes(t)) {
      drenar(semDreno);
      semDreno = 0;
    }
    if (instantesFisico.includes(t)) registrarFisico();
    vez = (1 - vez) as Lado;
  }

  const somaPosse = estat.posse[0] + estat.posse[1];
  const posse: Par = somaPosse > 0 ? [Math.round((100 * estat.posse[0]) / somaPosse), 100 - Math.round((100 * estat.posse[0]) / somaPosse)] : [50, 50];
  estat.posse = posse;

  // a energia com que cada um terminou (ou saiu) é o cansaço que ele leva para a rodada seguinte
  for (const l of lados) for (const p of l.part.values()) p.energiaFim = Math.round(l.en.get(p.jogadorId) ?? 0);

  // notas do jogo: base 6, gols, assistências, cartões, resultado do time, goleiro (jogo sem sofrer gol) e um pouco de sorte
  for (const l of lados) {
    const lado = l === lados[0] ? 0 : 1;
    const pro = lados[lado].gols;
    const contra = lados[1 - lado]?.gols ?? 0;
    for (const p of l.part.values()) {
      let nota = 6 + 1.1 * p.gols + 0.6 * p.assistencias - 0.3 * p.amarelos - (p.vermelho ? 1.5 : 0);
      nota += pro > contra ? 0.4 : pro < contra ? -0.4 : 0;
      if (l.slot.get(p.jogadorId) === 'G') nota += contra === 0 ? 0.9 : -0.3 * Math.min(contra, 4);
      nota += (rng.proximo() - 0.5) * 1.2;
      p.nota = Math.round(Math.min(10, Math.max(3, nota)) * 10) / 10;
    }
  }

  return {
    gols: [lados[0].gols, lados[1].gols],
    eventos,
    estat,
    participacoes: [...lados[0].part.values(), ...lados[1].part.values()].filter((p) => p.entrou),
    totalMinutos: total,
    fimPrimeiroTempo: fim1,
    emCampoFinal: [lados[0].campo.map((j) => j.id), lados[1].campo.map((j) => j.id)],
    escalacaoInicial: [inicial(esc[0]), inicial(esc[1])],
    fisico,
  };
}
